import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { logError } from '../../../utils/logger';
import { Card } from '../../shared/components';
import { projectLabel, Register, siteVisitsApi } from '../siteVisits.api';

const fmt = (d: string | null) => (d ? dayjs(d).format('DD MMM YYYY') : '—');

/** "3 / 2" with the extra visits beyond the quotation's free ones called out. */
function Visits({ done, free, extra }: { done: number; free: number | null; extra: number }) {
  return (
    <Stack direction='row' gap={0.75} alignItems='baseline' justifyContent='center'>
      <span>
        {done}
        <Typography component='span' variant='caption' color='text.secondary'>
          {' '}/ {free ?? '—'}
        </Typography>
      </span>
      {extra > 0 && (
        <Typography component='span' variant='caption' sx={{ color: 'error.main', fontWeight: 700 }}>
          +{extra} chargeable
        </Typography>
      )}
    </Stack>
  );
}

/**
 * The site visit register: per project, visits done against the free visits in its quotation
 * (extras are chargeable), and per engineer, what was scheduled, done and is still pending.
 */
export default function RegisterTab() {
  const [from, setFrom] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'));
  const [data, setData] = useState<Register | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    siteVisitsApi
      .register({ from, to })
      .then((r) => live && setData(r))
      .catch((e) => live && setError(logError({ message: 'Could not load the register', error: e, code: 'SITE_VISIT_REGISTER_FAILED' })))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [from, to]);

  return (
    <Stack gap={3}>
      <Card sx={{ py: 2 }}>
        <Stack direction='row' gap={2} flexWrap='wrap' alignItems='center'>
          <TextField label='From' type='date' size='small' value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label='To' type='date' size='small' value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Typography variant='caption' color='text.secondary' sx={{ maxWidth: 420 }}>
            Scheduled, cancelled, issues and last visit follow the dates. Visits done against free visits count the
            whole project, so extras show correctly.
          </Typography>
        </Stack>
      </Card>
      {error && <Alert severity='error'>{error}</Alert>}
      {loading && <LinearProgress />}

      <Card sx={{ p: 0 }}>
        <Typography variant='subtitle1' fontWeight={600} sx={{ px: 2, pt: 2 }}>By project</Typography>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell align='center'>Scheduled</TableCell>
                <TableCell align='center'>Done</TableCell>
                <TableCell align='center'>Cancelled</TableCell>
                <TableCell align='center'>Engineer visits / free</TableCell>
                <TableCell align='center'>Director visits / free</TableCell>
                <TableCell align='center'>Issues</TableCell>
                <TableCell>Last visit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.projects.map((p) => (
                <TableRow key={p.jobId} hover
                  sx={p.extraEngineerVisits + p.extraDirectorVisits > 0 ? { bgcolor: 'rgba(220, 38, 38, 0.05)' } : undefined}>
                  <TableCell>{projectLabel(p.projectCode, p.projectName)}</TableCell>
                  <TableCell align='center'>{p.scheduled}</TableCell>
                  <TableCell align='center'>{p.completed}</TableCell>
                  <TableCell align='center'>{p.cancelled}</TableCell>
                  <TableCell align='center'>
                    <Visits done={p.engineerVisits} free={p.freeEngineerVisits} extra={p.extraEngineerVisits} />
                  </TableCell>
                  <TableCell align='center'>
                    <Visits done={p.directorVisits} free={p.freeDirectorVisits} extra={p.extraDirectorVisits} />
                  </TableCell>
                  <TableCell align='center'>
                    <Box component='span' sx={p.issues ? { color: 'error.main', fontWeight: 600 } : undefined}>{p.issues}</Box>
                  </TableCell>
                  <TableCell>{fmt(p.lastVisitDate)}</TableCell>
                </TableRow>
              ))}
              {data && !data.projects.length && (
                <TableRow>
                  <TableCell {...({ colSpan: 8 } as object)} align='center'>No visits in this period.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Card sx={{ p: 0 }}>
        <Typography variant='subtitle1' fontWeight={600} sx={{ px: 2, pt: 2 }}>By engineer</Typography>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Engineer</TableCell>
                <TableCell align='center'>Scheduled</TableCell>
                <TableCell align='center'>Done</TableCell>
                <TableCell align='center'>Pending (past date)</TableCell>
                <TableCell align='center'>Issues found</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.engineers.map((e) => (
                <TableRow key={e.engineerId} hover>
                  <TableCell>{e.engineerName ?? '—'}</TableCell>
                  <TableCell align='center'>{e.scheduled}</TableCell>
                  <TableCell align='center'>{e.completed}</TableCell>
                  <TableCell align='center'>
                    <Box component='span' sx={e.pending ? { color: 'warning.main', fontWeight: 700 } : undefined}>{e.pending}</Box>
                  </TableCell>
                  <TableCell align='center'>{e.issues}</TableCell>
                </TableRow>
              ))}
              {data && !data.engineers.length && (
                <TableRow>
                  <TableCell {...({ colSpan: 5 } as object)} align='center'>No visits in this period.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Stack>
  );
}
